import { useEffect, useState } from "react";
import { Button, ButtonGroup, Card, CardBody, Col, FormGroup, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import Select from "react-select";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import './styles.css';
import countries from "../../../../data/countries";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { get, post } from "../../../../utils/helpers/api";
import DefaultButton from "../../../../components/Buttons/Default";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n, { changeLanguage } from "../../../../i18n";
import { useSettings } from "../../../../context/settings";
import NotificationsTab from "./Tabs/Notifications";

function PreferencesPage() {
  const { t } = useTranslation();
  const { updateSettings, settings } = useSettings();
  const [activeTab, setActiveTab] = useState('localization');
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [settingsData, setSettingsData] = useState();
  const [preferencesData, setPreferencesData] = useState({
    language: i18n.language || 'en',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    email_notifications: true,
    push_notifications: true,
    base_currency: 'USD',
    default_tax_rate: 0,
    default_payment_terms: 'due_on_receipt'
  });

  const TABS = [
    { id: 'localization',  label: t('preferences.localization') },
    { id: 'notifications', label: t('preferences.notifications') },
    { id: 'defaults',      label: t('preferences.defaults') },
  ];

  const getSettings = async () => {
    setPreferencesLoading(true);
    const data = await get('/settings/preferences');
    if(data.success) {
      setSettingsData(data);
      setPreferencesData({
        base_currency: data.company_settings?.sales?.base_currency || 'USD',
        default_tax_rate: data.company_settings?.sales?.default_tax_rate || 0,
        default_payment_terms: data.company_settings?.sales?.default_payment_terms || 'due_on_receipt'
      });
      if (data.user_settings) updateSettings(data.user_settings);
      if (data.company_settings) updateSettings(data.company_settings);
    }
    setPreferencesLoading(false);
  }

  useEffect(() => {
    getSettings();
  }, []);

  const saveLocalization = async () => {
    setSaveLoading(true);
    const data = await post('/settings/preferences/localization', {
      language: preferencesData.language,
      date_format: preferencesData.date_format,
      time_format: preferencesData.time_format
    });
    if(data.success) {
      toast.success(t('preferences.localizationSaved'));
      changeLanguage(preferencesData.language);
      updateSettings({ app: { date_format: preferencesData.date_format, time_format: preferencesData.time_format, language: preferencesData.language } });
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  const saveDefaults = async () => {
    setSaveLoading(true);
    const data = await post('/settings/preferences/sales', {
      base_currency: preferencesData.base_currency,
      default_tax_rate: preferencesData.default_tax_rate,
      default_payment_terms: preferencesData.default_payment_terms
    });
    if(data.success) {
      toast.success(t('preferences.defaultsSaved'));
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('preferences.title')} />

        <div className="subheader-page-content scrollable">
        <p className="text-muted mb-3">{t('preferences.subtitle')}</p>

        <Row className="align-items-start">
          {/* Left nav */}
          <Col lg="2">
            <Nav pills vertical className="flex-column pref-nav">
              {TABS.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="cursor-pointer"
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </Col>

          {/* Right content */}
          <Col lg="10">
            <TabContent activeTab={activeTab} className="pref-tab-content">

              {/* Localization */}
              <TabPane tabId="localization">
                <Card className="mb-3">
                  <CardBody>
                    <p className="text-muted small fw-semibold text-uppercase mb-3">
                      {t('preferences.localization')}
                      <span className="badge bg-secondary ms-2">{t('preferences.userBadge')}</span>
                    </p>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('preferences.language')}</Label>
                          <Input 
                            type="select" 
                            value={preferencesData.language} 
                            onChange={(e) => setPreferencesData({ ...preferencesData, language: e.target.value })}
                          >
                            <option value="en">English</option>
                            <option value="ar">العربية</option>
                            <option value="fr">Français</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('preferences.dateFormat')}</Label>
                          <Input type="select" value={preferencesData.date_format} onChange={(e) => setPreferencesData({ ...preferencesData, date_format: e.target.value })}>
                            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                            <option value="MM-DD-YYYY">MM-DD-YYYY</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="MM.DD.YYYY">MM.DD.YYYY</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                            <option value="YYYY/MM/DD">YYYY/MM/DD</option>
                            <option value="YYYY.MM.DD">YYYY.MM.DD</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup className="mb-0">
                      <Label>{t('preferences.timeFormat')}</Label>
                      <div>
                        <ButtonGroup>
                          <Button color={preferencesData.time_format === '12h' ? 'primary' : 'light'} size="sm" onClick={() => setPreferencesData({ ...preferencesData, time_format: '12h' })}>{t('preferences.time12h')}</Button>
                          <Button color={preferencesData.time_format === '24h' ? 'primary' : 'light'} size="sm" onClick={() => setPreferencesData({ ...preferencesData, time_format: '24h' })}>{t('preferences.time24h')}</Button>
                        </ButtonGroup>
                      </div>
                    </FormGroup>
                  </CardBody>
                </Card>
                <DefaultButton color="primary" size="sm" disabled={saveLoading} loading={saveLoading} onClick={saveLocalization}>
                  {t('common.save')}
                </DefaultButton>
              </TabPane>

              {/* Notifications */}
              <NotificationsTab settingsData={settingsData} />

              {/* Defaults */}
              <TabPane tabId="defaults">
                <Card className="mb-3">
                  <CardBody>
                    <p className="text-muted small fw-semibold text-uppercase mb-3">
                      {t('preferences.businessDefaults')}
                      <span className="badge bg-primary ms-2">{t('preferences.companyBadge')}</span>
                    </p>

                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <Label>{t('preferences.baseCurrency')}</Label>
                          <Input 
                            type="text" 
                            value={preferencesData.base_currency} 
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup>
                          <Label>{t('preferences.defaultTaxRate')}</Label>
                          <Input
                            type="number" min="0" max="100" step="0.5" placeholder={t('preferences.taxRatePlaceholder')}
                            value={preferencesData.default_tax_rate}
                            onChange={(e) => setPreferencesData({ ...preferencesData, default_tax_rate: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup className="mb-0">
                          <Label>{t('preferences.defaultPaymentTerms')}</Label>
                          <Input type="select" value={preferencesData.default_payment_terms} onChange={(e) => setPreferencesData({ ...preferencesData, default_payment_terms: e.target.value })}>
                            <option value="due_on_receipt">{t('preferences.dueOnReceipt')}</option>
                            <option value="net7">{t('preferences.net7')}</option>
                            <option value="net15">{t('preferences.net15')}</option>
                            <option value="net30">{t('preferences.net30')}</option>
                            <option value="net60">{t('preferences.net60')}</option>
                            <option value="net90">{t('preferences.net90')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <small className="text-danger">{t('preferences.baseCurrencyDescription')}</small>
                  </CardBody>
                </Card>
                <DefaultButton color="primary" size="sm" disabled={saveLoading} loading={saveLoading} onClick={saveDefaults}>
                  {t('common.save')}
                </DefaultButton>
              </TabPane>

            </TabContent>
          </Col>
        </Row>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default PreferencesPage;
